import React, { useMemo } from 'react';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';
import EnhancedMarkdown from '@resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import VerticalContent from '@resources/js/components/VerticalContent/VerticalContent';
import Table, {
  TableColumn,
  TableProps,
} from '@resources/js/components/Table/Table';
import { Cell } from 'react-table';
import {
  IconDefaultsContextProvider,
  iconSizeLarge,
} from '@resources/js/contexts/useIconDefaults';
import millisecondsToMinutesAndSeconds from '@resources/js/helpers/millisecondsToMinutesAndSeconds';
import useTheme from '@resources/js/contexts/useTheme';
import SiteLogo from '@resources/js/components/SiteLogo/SiteLogo';
import BuildsNoDataPlaceholder from '@resources/js/components/BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import useBuildsRotationFacets from '@resources/js/contexts/useBuildsRotationFacets';

export type BuildsRotationSampleSectionViewModel = {
  title: string;
  description: string;
  table: {
    columns: Record<ColumnKey, TableColumn | null | undefined>;
    data: Row[];
    footer: Row;
  };
};

type ColumnKey = 'name' | 'startTime' | 'duration' | 'link';

type Row = {
  facetId: string;
  name: string;
  startTime: number;
  duration: number;
  link: string;
  metricValueForDisplay: string;
};

export default function BuildsRotationSampleSection({
  title,
  description,
  table,
}: BuildsRotationSampleSectionViewModel): JSX.Element | null {
  const theme = useTheme();
  const { facet } = useBuildsRotationFacets();
  const facetToFilterTo = facet?.value ?? table?.data[0]?.facetId;

  const data = useMemo(
    () => table?.data.filter((x) => x.facetId === facetToFilterTo) ?? [],
    [facetToFilterTo, table?.data]
  );

  const columns = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(table?.columns ?? {}).map(([accessor, column]) => [
          accessor,
          {
            ...column,
            renderCell,
          } as TableColumn<Row>,
        ])
      ),
    [table?.columns]
  );

  if (!title) {
    return null;
  }

  if (!table) {
    return (
      <VerticalContent className='builds-rotation-sample-section'>
        <EnhancedMarkdown
          markdown={`
# ${title}

${description}
`}
          overrides={buildsEnhancedMarkdownOverrides}
        />
        <BuildsNoDataPlaceholder />
      </VerticalContent>
    );
  }

  const truncate: TableProps['truncate'] = {
    limit: 5,
    showMoreButton: { styles: theme.primaryButtonStyles },
  };

  return (
    <IconDefaultsContextProvider
      overrides={{
        actor: {
          size: iconSizeLarge,
          bordered: true,
        },
      }}
    >
      <VerticalContent className='builds-rotation-sample-section'>
        <EnhancedMarkdown
          markdown={`
# ${title}

${description}
`}
          overrides={buildsEnhancedMarkdownOverrides}
        />
        <Table {...table} data={data} columns={columns} truncate={truncate} />
      </VerticalContent>
    </IconDefaultsContextProvider>
  );
}

const renderCell = (cell: Cell<Row>) => {
  switch (cell.column.id) {
    case 'name':
      return (
        <a href={cell.row.original.link} target={'_blank'} rel='noreferrer'>
          <EnhancedMarkdown markdown={cell.value} />
        </a>
      );
    case 'startTime':
      return new Date(cell.value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    case 'duration':
      return millisecondsToMinutesAndSeconds(cell.value);
    case 'link':
      return (
        <a href={cell.value} target={'_blank'} rel='noreferrer'>
          <SiteLogo size={24} />
        </a>
      );
    case 'metricValue':
      return cell.row.original.metricValueForDisplay;
    default:
      return cell.value;
  }
};
